import { ProtectedComponent, UpgradeSubscriptionModal } from '@components'

import { Flex, Spacing, Tile, TileSize, useModal } from '@billy/design-system'
import { useCallback } from 'react'

import type { UiSourceValue } from '@features/reconciliation/enums'
import { useCanConnectToShineBank } from '@features/shineBanking/hooks/useCanConnectToShineBank'
import { useInitiateShineConnection } from '@features/shineBanking/hooks/useInitiateShineConnection'

import { LogoShineText } from '../../../../assets/images'
import { ModalId } from '../../../../enums/modalId'
import { Scope } from '../../../../enums/scope'
import { TrackingContext } from '../../../../enums/trackingContext'
import { useComponentId, useSegment } from '../../../../hooks'
import { useBankConnectionLimit } from '../../hooks/useBankConnectionLimit'
import { useInitiateOpenBankingConnection } from '../../hooks/useInitiateOpenBankingConnection'
import { PopularBankConfig } from '../../utils/getPopularBanksData'
import * as Styled from './styles'

interface BankTilesListProps {
  banks: PopularBankConfig[]
  onSearchPrefill?: (bankName: string) => void
  tileSize?: TileSize
  uiSource?: UiSourceValue
}

export const PopularBanksTilesList = ({ banks, onSearchPrefill, tileSize = 'flex', uiSource }: BankTilesListProps) => {
  const { connectToOpenBanking } = useInitiateOpenBankingConnection(uiSource)
  const { isLimitReached } = useBankConnectionLimit()
  const upgradeModalId = useComponentId(ModalId.UpgradeSubscriptionModal)
  const { open: openUpgradeModal } = useModal(upgradeModalId)
  const { track } = useSegment()
  const { canConnectToShineBank } = useCanConnectToShineBank()
  const { initiateShineConnection } = useInitiateShineConnection()

  const handleShineTileClick = useCallback(() => {
    initiateShineConnection()
    track('XXX - Reconciliation Bank Connection Initiated (Client)')
  }, [initiateShineConnection, track])

  const handleTileClick = useCallback(
    (bank: PopularBankConfig) => {
      if (bank.multiple && onSearchPrefill) {
        onSearchPrefill(bank.searchTerm)
      } else {
        if (isLimitReached) {
          openUpgradeModal()
          return
        }

        connectToOpenBanking(bank.id)
        track('XXX - Reconciliation Bank Connection Initiated (Client)')
      }
    },
    [connectToOpenBanking, isLimitReached, onSearchPrefill, openUpgradeModal, track],
  )

  const tiles = banks.map((bank) => (
    <Tile key={bank.id} size={tileSize} onClick={() => handleTileClick(bank)}>
      <Styled.ImageContainer src={bank.presentation || ''} alt={bank.name ?? bank.searchTerm ?? ''} />
    </Tile>
  ))

  if (canConnectToShineBank) {
    tiles.unshift(
      <ProtectedComponent key="shine" scopes={Scope.IdentityMergeWrite}>
        <Tile size={tileSize} onClick={handleShineTileClick}>
          <Styled.ShineLogoFrame>
            <LogoShineText />
          </Styled.ShineLogoFrame>
        </Tile>
      </ProtectedComponent>,
    )
  }

  return (
    <>
      {tileSize === 'flex' ? (
        <Styled.TilesGrid>{tiles}</Styled.TilesGrid>
      ) : (
        <Flex gap={Spacing.S8} flexWrap="wrap" justifyContent="center">
          {tiles}
        </Flex>
      )}
      <UpgradeSubscriptionModal id={upgradeModalId} trackingContext={TrackingContext.BankConnectionLimitsExceeded} />
    </>
  )
}
